import React, { useEffect } from 'react';

import { observer } from 'mobx-react';
import { useForm } from 'react-hook-form';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useUnmount } from 'react-use';

import { InfoBox, LbInput, LbPagination, InputAdornment } from '@infoods/lifebase-ui';
import classes from './PaymentHistoryList.module.scss';

import { EmptyList } from '../../../../shared/components/ui-components';
import { LbHorizontalScrollContainer } from '../../../../shared/components/LbHorizontalScrollContainer/LbHorizontalScrollContainer';
import { HeaderRow } from './HeaderRow/HeaderRow';
import { paymentHistoryListStore } from '../../../stores/paymentHistoryListStore';

import { PaymentRecordRow } from './PaymentRecordRow/PaymentRecordRow';
import { useAutoSubmitForm } from '../../../../shared/hooks/useAutoSubmitForm';
import { PaymentHistoryFilterFormValues } from '../../../interfaces/paymentHistoryList';
import { RevenueGeneralFilterValues } from '../DetailedRevenue.settings';

type PaymentHistoryListProps = {
  entityId: number;
  generalFilters: RevenueGeneralFilterValues;
};

type PaymentHistoryListFromValues = {
  customerNameOrEmail: string;
};

const PaymentHistoryList = observer(
  ({ entityId, generalFilters }: PaymentHistoryListProps) => {
    const { control, handleSubmit, watch, getValues } =
      useForm<PaymentHistoryListFromValues>();

    const { paymentsList, filterMethods, getPaymentsList, setDefaultValues } =
      paymentHistoryListStore;

    const getFilterData = (
      formData: PaymentHistoryListFromValues,
    ): PaymentHistoryFilterFormValues => ({
      generalFilter: { ...generalFilters },
      paymentHistoryFilter: { ...formData },
    });

    const handleFormSubmit = handleSubmit((formData) => {
      const filterData = getFilterData(formData);

      filterMethods.applyFilters(filterData);
    });

    useAutoSubmitForm(handleFormSubmit, watch, ['customerNameOrEmail']);

    useEffect(() => {
      filterMethods.resetFilterData({
        generalFilter: { ...generalFilters },
        paymentHistoryFilter: { customerNameOrEmail: '' },
      }); // fix show correct empty list placeholder
      const formData = getValues();
      const filterData = getFilterData(formData);

      getPaymentsList(entityId, filterData);
    }, [entityId, generalFilters]);

    useUnmount(() => {
      setDefaultValues();
    });

    return (
      <InfoBox
        title="Payment History"
        adornment={
          <LbInput
            control={control}
            name="customerNameOrEmail"
            variant="outlined"
            size="small"
            placeholder="Search by name or email"
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchRoundedIcon fontSize="large" color="tertiary" />
                </InputAdornment>
              ),
            }}
          />
        }
        customContentPadding
      >
        <LbHorizontalScrollContainer
          variant="inBox"
          staticContent={
            <EmptyList
              show={filterMethods.isShowPlaceholder}
              isSearch={filterMethods.isSearchUsed}
              description="There are no payments"
            />
          }
        >
          <div className={classes.paymentHistoryTable}>
            <HeaderRow />
            {paymentsList.map((paymentRecord) => (
              <PaymentRecordRow
                key={paymentRecord.payment.id}
                paymentRecord={paymentRecord}
              />
            ))}
          </div>
        </LbHorizontalScrollContainer>
        <LbPagination
          {...filterMethods.getPaginationProps()}
          elementsPerPage={filterMethods.elementsPerPage}
          totalElements={filterMethods.totalElements}
          variant="inBox"
        />
      </InfoBox>
    );
  },
);

export { PaymentHistoryList };
